import React, { useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _pick from 'lodash/pick';
import { withBus } from 'react-bus';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsCustomEvents from 'highcharts-custom-events';
import saveAs from 'file-saver';
import invariant from 'invariant';

import colors from 'src/utils/colors';
import MenuButton from 'src/components/common/Buttons/MenuButton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  DocumentIcon,
  ContentIcon,
  SubCategoryIcon,
  ChevronIcon,
  CompareScaleIcon,
  CrossIcon
} from 'src/components/SvgIcons';
import './GenericChart.scss';
import './Legend.scss';
import { enhanceChartPropsWithDefaults, getEnhancedPlotOptions } from './chartPropDefaults';
import convertLineChartSeriesToDelimitedData from './SeriesConverters/lineChart';
import { track } from 'src/utils/mixpanel';
import { isDrive } from 'src/utils/app';
import domtoimage from 'src/utils/dom-to-imagev2';
import { useSnackbarState } from 'src/providers/SnackbarProvider';

// Load Highcharts modules
require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);
require('highcharts/modules/map')(Highcharts);
require('highcharts/modules/venn')(Highcharts);

HighchartsMore(Highcharts);
HighchartsCustomEvents(Highcharts);

const HORIZONTAL_SCROLL_THRESHOLD = 8;

const maybeRenderTitleTooltip = (chartProps) => {
  if (!chartProps.title || !chartProps.title.text || !chartProps.title.TooltipComponent) {
    return null;
  }

  const { text, TooltipComponent } = chartProps.title;
  return (
    <TooltipComponent placement="right-end">
      <div
        style={{
          position: 'absolute',
          zIndex: 2,
          fontSize: 28,
          fontFamily: '"Roboto", sans-serif',
          top: -7,
          left: 9,
          cursor: 'default',
          color: 'transparent'
        }}
      >
        {text}
      </div>
    </TooltipComponent>
  );
};

const GenericChart = (props) => {
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const uniqChartId = useRef(btoa(Math.random().toString()));

  const getChartOptions = useCallback(
    (chart) => {
      const { chartProps, chartSeries, app } = chart;

      if (!chartProps.xAxis || chartProps.xAxis.length === 0) {
        chartProps.xAxis = [{}];
      }
      if (!chartProps.yAxis || chartProps.yAxis.length === 0) {
        chartProps.yAxis = [{}];
      }

      const enhancedChartProps = enhanceChartPropsWithDefaults(
        { ...chartProps, tab: _get(app, 'queryParams.tab', ''), subtab: _get(app, 'queryParams.subtab', '') },
        uniqChartId.current,
        props.eventBus
      );
      const enhancedPlotOptions = getEnhancedPlotOptions(chartProps);

      if (chartProps.chart.type === 'areaspline') {
        chartSeries.forEach((series) => {
          if (series.data.filter((dp) => dp[1] !== null).length < 2) {
            series.marker.enabled = true;
          }
        });
      }

      return {
        ...enhancedChartProps,
        plotOptions: enhancedPlotOptions,
        series: chartSeries,
        noData: {
          style: {
            fontWeight: '400',
            fontSize: '20px',
            color: colors.quartz
          }
        }
      };
    },
    [props.eventBus]
  );

  const saveChart = async () => {
    track('saved chart as image');
    const blob = (await domtoimage.toBlob(chartContainerRef.current, { additionalOptions: { scale: 2 } })) as Blob;
    saveAs(blob, 'chart_image.png');
  };

  const exportCsvData = async () => {
    track('export chart as csv');

    const propsForFetch = [
      props.chartSeries,
      props.comparisonTimePeriod.id === 'prior-period',
      props.comparisonTimePeriod,
      props.mainTimePeriod
    ];

    const data = await props.convertSeriesToDelimitedData(...propsForFetch);

    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, 'chart_data.csv');
  };

  const moveLeft = (steps) => {
    const { dataMin } = chartInstanceRef.current?.chart.xAxis[0].getExtremes();
    let { min, max } = chartInstanceRef.current?.chart.xAxis[0].getExtremes();

    if (min - steps < dataMin) {
      max -= min - dataMin;
      min = dataMin;
    } else {
      min -= steps;
      max -= steps;
    }

    chartInstanceRef.current.chart.xAxis[0].setExtremes(min, max);
  };

  const createChart = async () => {
    useSnackbarState.setState((prev) => ({ ...prev, open: true, message: 'Copying image...', type: 'success' }));
    try {
      const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      const blob = (await domtoimage.toBlob(chartContainerRef.current, { additionalOptions: { scale: 2 } })) as Blob;

      if (isFirefox) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'image.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        const clipboardItem = new ClipboardItem({ 'image/png': blob });
        navigator.clipboard.write([clipboardItem]);
      }

      useSnackbarState.setState((prev) => ({
        ...prev,
        open: true,
        message: 'Copied image!',
        type: 'success',
        timeout: 3000
      }));
    } catch (e) {
      console.error('Error copying image to clipboard', e);
      useSnackbarState.setState((prev) => ({
        ...prev,
        open: true,
        message: 'Image copy failure',
        type: 'error',
        timeout: 3000
      }));
    }
  };

  const getExportButtonInfo = useCallback(
    (chartProps) => {
      const exportMenu = {
        mainButton: {
          icon: <MoreHorizIcon style={{ color: colors.darkBlue }} />,
          direction: 'down' as 'down'
        },
        subItems: [
          { icon: <ContentIcon />, text: 'Save image', onClick: () => saveChart() },
          { icon: <DocumentIcon />, text: 'Export CSV', onClick: () => exportCsvData() },
          {
            icon: <SubCategoryIcon />,
            text: 'Copy image',
            onClick: () => createChart()
          }
        ]
      };
      if (_get(chartProps, ['exporting', 'showCompare'])) {
        exportMenu.subItems.push({
          icon: <CompareScaleIcon />,
          text: 'Compare',
          onClick: () => props.eventBus.emit('showCompare')
        });
        if (isDrive && window.location.search.includes('ctype')) {
          exportMenu.subItems.push({
            icon: <CrossIcon />,
            text: 'Clear Comparison',
            onClick: () => props.eventBus.emit('clearComparison')
          });
        }
      }
      return exportMenu;
    },
    [exportCsvData, props.eventBus]
  );

  const moveRight = (steps) => {
    const { loadNextPage } = props;
    const { dataMax } = chartInstanceRef.current?.chart.xAxis[0].getExtremes();
    let { min, max } = chartInstanceRef.current?.chart.xAxis[0].getExtremes();

    if (max === dataMax) {
      loadNextPage();
    } else if (max + steps > dataMax) {
      min += dataMax - max;
      max = dataMax;
    } else {
      min += steps;
      max += steps;
    }
    if (chartInstanceRef.current?.chart) {
      chartInstanceRef.current.chart.xAxis[0].setExtremes(min, max);
    }
  };

  const renderExportMenu = (chartProps) => {
    if (_get(chartProps, ['exporting', 'enabled'])) {
      const menuInfo = getExportButtonInfo(chartProps);
      return <MenuButton menuInfo={menuInfo} />;
    }
    return null;
  };

  const renderHorizontalScroll = (chartProps) => {
    if (!_get(chartProps, ['horizontalScrolling', 'enabled'])) {
      return null;
    }
    const scrollStyles = _get(chartProps, ['horizontalScrolling', 'scrollStyles'], {});
    const { step } = chartProps.horizontalScrolling;

    invariant(
      !_isNil(step),
      '`chartProps.horizontalScrolling.step` must be set when `chartProps.horizontalScrolling.enabled` is set.'
    );

    return (
      <div className="chart-horizontal-scrolling" style={{ ...scrollStyles }}>
        <button onClick={() => moveLeft(step)}>
          <ChevronIcon style={{ stroke: colors.darkBlue, height: '40px', width: '40px', transform: 'rotate(90deg)' }} />
        </button>
        <button onClick={() => moveRight(step)}>
          <ChevronIcon
            style={{ stroke: colors.darkBlue, height: '40px', width: '40px', transform: 'rotate(-90deg)' }}
          />
        </button>
      </div>
    );
  };

  const shouldRenderHorizontalScroll = (axis, chartSettings) => {
    const axisArray = _get(axis, [0, 'categories'], []);
    const shouldScroll = _get(chartSettings, 'horizontalScrolling.enabled', false);
    return axisArray.length > HORIZONTAL_SCROLL_THRESHOLD || shouldScroll;
  };

  useEffect(() => {
    if (chartInstanceRef.current?.chart && props.doChartRefreshAfterUpdate) {
      chartInstanceRef.current.chart.update({ series: props.chartSeries });
      chartInstanceRef.current.chart.redraw();
    }
  }, [props.chartSeries, props.doChartRefreshAfterUpdate]);

  const { className, chartSeries, chartProps } = props;
  if (!chartSeries || !chartProps) {
    return null;
  }
  const chartOptions = getChartOptions(props);
  const { xAxis } = chartProps;
  return (
    <div className={`line-chart ${className}`} style={{ position: 'relative' }}>
      {renderExportMenu(chartProps)}
      {shouldRenderHorizontalScroll(xAxis, chartProps) && renderHorizontalScroll(chartProps)}
      {maybeRenderTitleTooltip(chartProps)}
      <div className="line-chart__container" ref={chartContainerRef}>
        <div>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartInstanceRef} />
        </div>
      </div>
    </div>
  );
};

GenericChart.defaultProps = {
  className: '',
  loadNextPage: () => {},
  convertSeriesToDelimitedData: convertLineChartSeriesToDelimitedData,
  doChartRefreshAfterUpdate: false
};

GenericChart.propTypes = {
  chartSeries: PropTypes.array.isRequired,
  chartProps: PropTypes.object.isRequired,
  convertSeriesToDelimitedData: PropTypes.func,
  eventBus: PropTypes.object.isRequired,
  className: PropTypes.string,
  loadNextPage: PropTypes.func,
  comparisonTimePeriod: PropTypes.object.isRequired,
  mainTimePeriod: PropTypes.object.isRequired,
  doChartRefreshAfterUpdate: PropTypes.bool
};

const mapStateToProps = (state) => _pick(state, ['comparisonTimePeriod', 'mainTimePeriod', 'app']);

export default withBus('eventBus')(connect(mapStateToProps)(GenericChart));
